import React from 'react';
import { useData } from '../hooks/useData';
import { LoadingSpinner } from '../components/Common';
import { DynamicIcon } from '../components/icons';
import { motion } from 'framer-motion';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    AreaChart, Area
} from 'recharts';

// --- Shared Components ---

const StatCard = ({ title, value, icon, color, delay, trend }) => {
    let trendColor = 'gray';
    let TrendIcon = 'Minus';
    let trendText = '-';

    if (trend) {
        if (trend.direction === 'up') {
            trendColor = 'green';
            TrendIcon = 'ArrowUp';
            trendText = `+${trend.percentage}%`;
        } else if (trend.direction === 'down') {
            trendColor = 'red';
            TrendIcon = 'ArrowDown';
            trendText = `-${trend.percentage}%`;
        } else {
            trendText = "0%";
            TrendIcon = 'Minus';
            trendColor = 'gray';
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: delay }}
            className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
        >
            <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-full bg-${color}-50 text-${color}-600 dark:bg-${color}-900/30 dark:text-${color}-400`}>
                    <DynamicIcon name={icon} className="h-6 w-6" />
                </div>
                <div className={`flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full bg-${trendColor}-100 text-${trendColor}-800 dark:bg-${trendColor}-900 dark:text-${trendColor}-300`}>
                    <DynamicIcon name={TrendIcon} className="h-3 w-3" />
                    <span>{trendText}</span>
                </div>
            </div>
            <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</h3>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
            </div>
        </motion.div>
    );
};

const QuickActionCard = ({ title, icon, color, onClick, description, delay }) => (
    <motion.button
        onClick={onClick}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, delay: delay }}
        className="flex flex-col items-center p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md hover:border-blue-200 dark:hover:border-blue-800 transition-all text-center group w-full"
    >
        <div className={`p-4 rounded-full bg-${color}-50 text-${color}-600 dark:bg-${color}-900/30 dark:text-${color}-400 mb-4 group-hover:scale-110 transition-transform`}>
            <DynamicIcon name={icon} className="h-8 w-8" />
        </div>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{title}</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
    </motion.button>
);

const ChartSection = ({ title, children, delay }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: delay }}
        className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 h-full"
    >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">{title}</h3>
        <div className="h-[300px] w-full">
            {children}
        </div>
    </motion.div>
);

const WelcomeBanner = ({ setActivePage, user }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-50 via-blue-50 to-white dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 p-8 shadow-sm border border-indigo-100 dark:border-gray-700 mb-8"
    >
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                    {/* Platform Administration */}
                    Welcome back, {user?.name || 'User'}!

                </h1>
                <p className="text-gray-600 dark:text-gray-300 mt-2 max-w-2xl text-lg">
                    Manage multi-tenancy, monitor system health, and configure global platform settings from one central hub.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                    <button
                        onClick={() => setActivePage && setActivePage('tenants')}
                        className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                    >
                        <DynamicIcon name="Plus" className="w-4 h-4 mr-2" />
                        Onboard Tenant
                    </button>
                    <button
                        onClick={() => setActivePage && setActivePage('settings')}
                        className="inline-flex items-center px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg border border-gray-200 shadow-sm transition-colors dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                        <DynamicIcon name="Settings" className="w-4 h-4 mr-2" />
                        Global Config
                    </button>
                </div>
            </div>

            {/* Decorative Icon/Illustration Placeholder */}
            <div className="hidden md:block opacity-10 transform translate-x-4">
                <DynamicIcon name="ShieldCheck" className="w-32 h-32 text-indigo-600 dark:text-indigo-400" />
            </div>
        </div>

        {/* Decorative Background Elements */}
        <div className="absolute top-0 right-0 -mt-10 -mr-10 h-64 w-64 rounded-full bg-indigo-500/5 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -mb-10 -ml-10 h-64 w-64 rounded-full bg-blue-500/5 blur-3xl pointer-events-none" />
    </motion.div>
);

// --- Main Page Component ---

const AdminDashboardPage = ({ setActivePage, user }) => {
    const { data: tenants, isLoading: tenantsLoading } = useData('/tenants', null, true); // Assuming token is handled or available
    const { data: partners, isLoading: partnersLoading } = useData('/partners', null, true);
    const { data: platformStats, isLoading: statsLoading } = useData('/insights/platform-stats', null, true);


    if (tenantsLoading || partnersLoading || statsLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <LoadingSpinner />
            </div>
        );
    }

    const tenantCount = tenants ? tenants.filter(t => t.id !== 'platform').length : 0;
    const partnerCount = partners ? partners.length : 0;

    // Helper to format bytes
    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    const getVal = (key) => platformStats?.[key]?.value || 0;
    const getTrend = (key) => platformStats?.[key]?.trend || null;

    const stats = [
        { title: 'Active Tenants', value: tenantCount, icon: 'Users', color: 'blue', trend: null },
        { title: 'Partners', value: partnerCount, icon: 'Briefcase', color: 'purple', trend: null },
        { title: 'Business Apps', value: getVal('business_apps'), icon: 'LayoutGrid', color: 'green', trend: getTrend('business_apps') },
        { title: 'Contents', value: getVal('contents'), icon: 'PlayCircle', color: 'red', trend: getTrend('contents') },
        { title: 'Total Data Models', value: platformStats?.datasets?.count?.value || 0, icon: 'Database', color: 'indigo', trend: platformStats?.datasets?.count?.trend },
        { title: 'S3 Storage Usage', value: formatBytes((getVal('storage_usage_gb') || 0) * 1024 * 1024 * 1024), icon: 'Server', color: 'pink', trend: getTrend('storage_usage_gb') },
        { title: 'Total API Calls', value: getVal('total_api_calls'), icon: 'Activity', color: 'orange', trend: getTrend('total_api_calls') },
    ];

    // Mock data for charts (replace with real historical data when available)
    const activityData = [
        { name: 'Mon', calls: 4000, runs: 2400 },
        { name: 'Tue', calls: 3000, runs: 1398 },
        { name: 'Wed', calls: 2000, runs: 9800 },
        { name: 'Thu', calls: 2780, runs: 3908 },
        { name: 'Fri', calls: 1890, runs: 4800 },
        { name: 'Sat', calls: 2390, runs: 3800 },
        { name: 'Sun', calls: 3490, runs: 4300 },
    ];

    const distributionData = [
        { name: 'Tenants', value: tenantCount },
        { name: 'Partners', value: partnerCount },
        { name: 'Apps', value: getVal('business_apps') },
    ];

    return (
        <div className="space-y-8 p-1">
            {/* Header Section */}
            <WelcomeBanner setActivePage={setActivePage} user={user} />

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                    <StatCard key={stat.title} {...stat} delay={index * 0.1} />
                ))}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                    <ChartSection title="System Activity (Last 7 Days)" delay={0.4}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={activityData}>
                                <defs>
                                    <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="colorRuns" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                                        <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6B7280' }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6B7280' }} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                                />
                                <Legend />
                                <Area type="monotone" dataKey="calls" stroke="#8884d8" fillOpacity={1} fill="url(#colorCalls)" name="API Calls" />
                                <Area type="monotone" dataKey="runs" stroke="#82ca9d" fillOpacity={1} fill="url(#colorRuns)" name="App Runs" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartSection>
                </div>
                <div className="lg:col-span-1">
                    <ChartSection title="Platform Distribution" delay={0.5}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={distributionData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
                                <XAxis type="number" hide />
                                <YAxis dataKey="name" type="category" width={80} axisLine={false} tickLine={false} tick={{ fill: '#6B7280' }} />
                                <Tooltip
                                    cursor={{ fill: 'transparent' }}
                                    contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                                />
                                <Bar dataKey="value" fill="#6366f1" radius={[0, 4, 4, 0]} barSize={32} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartSection>
                </div>
            </div>

            {/* Quick Actions */}
            <div>
                <motion.h2
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="text-xl font-semibold text-gray-900 dark:text-white mb-6"
                >
                    Quick Actions
                </motion.h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <QuickActionCard
                        title="Manage Tenants"
                        description="Onboard new tenants, manage subscriptions, and monitor usage."
                        icon="Users"
                        color="blue"
                        onClick={() => setActivePage('tenants')}
                        delay={0.7}
                    />
                    <QuickActionCard
                        title="Manage Business Apps"
                        description="Configure app catalogs, deploy new versions, and manage access."
                        icon="LayoutGrid"
                        color="green"
                        onClick={() => setActivePage('business_apps')}
                        delay={0.8}
                    />
                    <QuickActionCard
                        title="System Settings"
                        description="Configure platform-wide settings, security policies, and integrations."
                        icon="Settings"
                        color="gray"
                        onClick={() => setActivePage('settings')}
                        delay={0.9}
                    />
                </div>
            </div>
        </div>
    );
};

export default AdminDashboardPage;